import { Clock, AlertTriangle } from 'lucide-react';

interface SlaBadgeProps {
  deadline: string | null | undefined;
  // Hours of remaining time below which we flag as urgent (default: 24h).
  urgentThresholdHours?: number;
  className?: string;
}

function formatRemaining(ms: number): string {
  const abs = Math.abs(ms);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs >= day) {
    const days = Math.floor(abs / day);
    const hours = Math.floor((abs % day) / hour);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (abs >= hour) {
    const hours = Math.floor(abs / hour);
    const mins = Math.floor((abs % hour) / minute);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const mins = Math.max(1, Math.floor(abs / minute));
  return `${mins}m`;
}

// Compact deadline badge for use in list rows. Computes status locally from the
// client clock — fine for at-a-glance views; the detail page uses the richer
// server-anchored <SlaCountdown /> instead.
export default function SlaBadge({
  deadline,
  urgentThresholdHours = 24,
  className = '',
}: SlaBadgeProps) {
  if (!deadline) return null;

  const ms = new Date(deadline).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;

  const isOverdue = ms < 0;
  const isUrgent = !isOverdue && ms < urgentThresholdHours * 60 * 60 * 1000;

  const styles = isOverdue
    ? 'bg-red-50 text-red-700 border-red-200'
    : isUrgent
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-green-50 text-green-700 border-green-200';

  const Icon = isOverdue ? AlertTriangle : Clock;
  const label = isOverdue ? `Overdue ${formatRemaining(ms)}` : formatRemaining(ms);

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md border ${styles} ${className}`}
      title={`Due ${new Date(deadline).toLocaleString()}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}
